// ISO 8601 week helpers for the homepage heat strip. UTC throughout: the
// backend keys weeks with to_char(ts, 'IYYY-"W"IW') in UTC, and the strip must
// agree with it cell for cell. No date library in the project (package.json).

const DAY_MS = 86400000;

/** '2026-W39' for a Date, in UTC. */
export const isoWeekOf = (date: Date): string => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // ISO weeks start on Monday; shift to the Thursday of the same week, whose year is the ISO year
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((d.getTime() - yearStart) / DAY_MS + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
};

/** The UTC Monday that starts the given ISO week; null when the string is not IYYY-Www. */
export const isoWeekStart = (isoWeek: string): Date | null => {
  const match = /^(\d{4})-W(\d{2})$/.exec(isoWeek);
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  if (week < 1 || week > 53) return null;
  // The Monday of week 1 is the Monday on or before January 4th
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4.getTime() - (jan4Day - 1) * DAY_MS);
  return new Date(week1Monday.getTime() + (week - 1) * 7 * DAY_MS);
};

/** 'Sep 21' for the tooltip's "Week of {date}"; the raw key when it cannot be parsed. */
export const formatWeekOf = (isoWeek: string): string => {
  const start = isoWeekStart(isoWeek);
  if (!start) return isoWeek;
  return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/** Sort key so 2025-W53 < 2026-W01. Strings compare correctly already; kept explicit for readers. */
export const compareIsoWeeks = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
